import type { Copro } from './Copro';
import type { Owner } from './Owner';
import type { User } from './User';

export const DPE_STEPS = {
  NEW: 'new',
  NO_OWNER: 'no-owner',
  VOLUNTARILY_UNTREATED: 'voluntarily-untreated',
  IDENTIFIED: 'identified',
  IN_PROCESS: 'in-process',
  PROCESSED: 'processed',
};

type DpeStepType = typeof DPE_STEPS[keyof typeof DPE_STEPS];

export const DPE_STEPS_LABELS = {
  [DPE_STEPS.NEW]: 'Nouveaux DPEs à identifier',
  [DPE_STEPS.NO_OWNER]: 'Aucun propriétaire',
  [DPE_STEPS.VOLUNTARILY_UNTREATED]: 'Non traité volontairement',
  [DPE_STEPS.IDENTIFIED]: 'Propriétaire(s) identifié(s)',
  [DPE_STEPS.IN_PROCESS]: 'En cours de traitement',
  [DPE_STEPS.PROCESSED]: 'Terminé',
};

// DPE Detail interface based on the API response
export interface DPEDetail {
  remoteId?: string;
  id?: string;
  dateEtablissementDpe?: string;
  hashkey?: string | null;
  version?: string;
  identifiantDpe?: string;
  desactive?: boolean;
  ancienDpeId?: string | null;
  dateReceptionDpe?: string;
  dpeXmlPath?: string;
  versionMoteurUsed?: string;
  versionXsdUsed?: string;
  statut?: string | null;
  isDpe2012?: boolean;
  dpe2012DonneesId?: string | null;
  enumTypeEnergieChauffagePrincipalId?: number;
  enumTypeEnergieEcsPrincipalId?: number;
  xmlHash?: string;
  suppressionEnCours?: boolean;
  dpeRemplacantId?: string | null;
  generateurChauffagePrincipalId?: string;
  anonymise?: boolean;
  migreEnUtc?: boolean;
  dateDerniereModification?: string;
  generateurEcsPrincipalId?: string;
  anneeConstruction?: number;
  appartementNonVisite?: number;
  enumCategorieErpId?: string | null;
  enumMethodeApplicationDpeLogId?: string | null;
  enumUsageFonctionnelBatimentId?: number;
  enumPeriodeConstructionId?: string | null;
  hsp?: string;
  nombreAppartement?: string;
  nombreNiveauImmeuble?: string;
  nombreNiveauLogement?: string;
  nombreOccupant?: string | null;
  shon?: string | null;
  surfaceHabitableImmeuble?: string;
  surfaceHabitableLogement?: string;
  surfaceUtile?: string | null;
  surfaceTertiaireImmeuble?: string | null;
  enumMethodeApplicationDpeTerId?: string | null;
  enumCalculEchantillonnageId?: string | null;
  administratifId?: string;
  idpar?: string | null;
  immatriculationCopropriete?: string | null;
  adresseBienId?: string;
  rplsLogId?: string | null;
  idBatimentRnb?: string | null;
  dpeId?: string;
  epConso5Usages?: string;
  epConso5UsagesM2?: string;
  epConsoAuxiliaireDistributionCh?: string;
  epConsoAuxiliaireDistributionEcs?: string;
  epConsoAuxiliaireDistributionFr?: string | null;
  epConsoAuxiliaireGenerationCh?: string;
  epConsoAuxiliaireGenerationChDepensier?: string;
  epConsoAuxiliaireGenerationEcs?: string;
  epConsoAuxiliaireGenerationEcsDepensier?: string;
  epConsoAuxiliaireVentilation?: string;
  epConsoCh?: string;
  epConsoChDepensier?: string;
  epConsoEclairage?: string;
  epConsoEcs?: string;
  epConsoEcsDepensier?: string;
  epConsoFr?: string;
  epConsoFrDepensier?: string;
  epConsoTotaleAuxiliaire?: string;
  classeBilanDpe?: string;
  adresseBrut?: string;
  banCity?: string;
  banCitycode?: string;
  banDateAppel?: string;
  banHousenumber?: string;
  banId?: string;
  banLabel?: string;
  banPostcode?: string;
  banScore?: string;
  banStreet?: string;
  banType?: string;
  banX?: string;
  banY?: string;
  codePostalBrut?: string;
  complEtageAppartement?: number;
  complNomResidence?: string;
  complRefBatiment?: string;
  complRefCageEscalier?: string;
  complRefLogement?: string;
  enumStatutGeocodageBanId?: number;
  labelBrut?: string;
  nomCommuneBrut?: string;
  banDepartement?: string;
  banErreur?: string | null;
  banRegion?: string;
  traiteParBatch?: boolean;
  banEpci?: string;
  labelBrutAvecComplement?: string;
  rooms?: string;
  [key: string]: unknown;
}


export interface DPE {
  id: number;
  remoteId: string;
  copro: Copro;
  treatedAt: Date;
  treatedBy: User;
  createdAt: Date;
  updatedAt: Date;
  owners: Owner[];
  DPEQualifier: User;
  coproId: number;
  coproName: string;
  coproAddress: string;
  assistant: string;
  newDPE: boolean;
  infoDPE: Record<string, unknown>;
  banLabel?: string;
  step: DpeStepType;
  details?: DPEDetail[];
  establishedAt?: Date | string;
  receivedAt?: Date | string;
}

